import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import { childService } from '../services';
import { Child } from '../models';
import { useAuthStore } from './auth';
import { useRealtimeSync } from '@/composables/useRealtimeSync';
import { useAppContextStore } from './appContext';

export const useChildrenStore = defineStore('children', () => {
  const children = ref<any[]>([]);
  const selectedChild = ref<any>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const authStore = useAuthStore();
  const realtimeSync = useRealtimeSync();
  let currentHouseholdId: string | null = null;

  // Actions
  async function loadChildren() {
    if (!authStore.user) return;

    loading.value = true;
    error.value = null;

    try {
      const appContextStore = useAppContextStore();
      const householdId = appContextStore.currentHousehold?.id;

      if (householdId) {
        children.value = await childService.getHouseholdChildren(householdId);
      } else {
        children.value = [];
      }
    } catch (err: any) {
      console.error('Error loading children:', err);
      error.value = err.message || 'Failed to load children';
      children.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function getChildById(childId: string) {
    loading.value = true;
    error.value = null;

    try {
      // First check if child is already in the store
      const existingChild = children.value.find(c => c.id === childId);
      if (existingChild) {
        selectedChild.value = existingChild;
        loading.value = false;
        return existingChild;
      }

      // If not, try to fetch it from the service
      const child = await childService.getChild(childId);
      if (child) {
        selectedChild.value = child;
      }
      return child;
    } catch (err: any) {
      console.error('Error getting child:', err);
      error.value = err.message || 'Failed to get child';
      return null;
    } finally {
      loading.value = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function createChild(childData: any, avatarFile?: File) {
    if (!authStore.user) throw new Error('User not authenticated');

    loading.value = true;
    error.value = null;

    try {
      const appContextStore = useAppContextStore();
      const household = appContextStore.currentHousehold;
      if (!household) {
        throw new Error('No household found for the current user.');
      }

      const newChild = await childService.createChild({
        name: childData.name,
        birth_date: childData.birthDate,
        household_id: household.id,
        avatar: childData.avatar,
        color: childData.color
      });

      children.value.push(newChild);

      // child_created est émis par childService.createChild, qui est le seul
      // point de passage commun à ce store et aux formulaires (ces derniers
      // appellent le service en direct sans passer par ici).

      return newChild;
    } catch (err: any) {
      console.error('Error creating child:', err);
      error.value = err.message || 'Failed to create child';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function updateChild(childId: string, childData: any, avatarFile?: File) {
    loading.value = true;
    error.value = null;

    try {
      const updatedChild = await childService.updateChild(childId, childData);

      const index = children.value.findIndex(c => c.id === childId);
      if (index !== -1) {
        children.value[index] = updatedChild;
      }

      if (selectedChild.value?.id === childId) {
        selectedChild.value = updatedChild;
      }

      return updatedChild;
    } catch (err: any) {
      console.error('Error updating child:', err);
      error.value = err.message || 'Failed to update child';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function deleteChild(childId: string) {
    loading.value = true;
    error.value = null;

    try {
      await childService.deleteChild(childId);

      children.value = children.value.filter(c => c.id !== childId);

      if (selectedChild.value?.id === childId) {
        selectedChild.value = null;
      }
    } catch (err: any) {
      console.error('Error deleting child:', err);
      error.value = err.message || 'Failed to delete child';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function selectChild(childId: string) {
    selectedChild.value = children.value.find(c => c.id === childId) || null;
  }

  // Realtime synchronization
  function startRealtimeSync(householdId: string) {
    // Éviter de créer plusieurs subscriptions pour le même household
    if (currentHouseholdId === householdId) {
      console.log('[Children Store] Already syncing this household, skipping')
      return
    }

    // Arrêter les anciennes subscriptions si on change de household
    if (currentHouseholdId) {
      stopRealtimeSync()
    }

    currentHouseholdId = householdId
    console.log('[Children Store] Starting realtime sync for household:', householdId)

    // Synchroniser la table children
    realtimeSync.subscribeToTable<Child>(
      `children-${householdId}`,
      'children',
      {
        onInsert: (payload) => {
          const newChild = payload.new as Child
          // Éviter les doublons
          if (!children.value.find(c => c.id === newChild.id)) {
            children.value.push(newChild)
            console.log('[Children Store] Child inserted via realtime:', newChild.name)
          }
        },
        onUpdate: (payload) => {
          console.log('[Children Store] onUpdate callback called')
          const updatedChild = payload.new as Child
          console.log('[Children Store] Updated child data:', updatedChild)

          const index = children.value.findIndex(c => c.id === updatedChild.id)
          console.log('[Children Store] Found index:', index, 'in', children.value.length, 'children')

          if (index !== -1) {
            children.value[index] = updatedChild
            console.log('[Children Store] Child updated via realtime:', updatedChild.name, 'points:', updatedChild.points, 'color:', updatedChild.color)

            // Si c'est l'enfant sélectionné, mettre à jour aussi selectedChild
            if (selectedChild.value && selectedChild.value.id === updatedChild.id) {
              selectedChild.value = updatedChild
              console.log('[Children Store] Selected child also updated')
            }
          }
        },
        onDelete: (payload) => {
          const deletedChild = payload.old as Child
          children.value = children.value.filter(c => c.id !== deletedChild.id)
          console.log('[Children Store] Child deleted via realtime:', deletedChild.id)

          // Si c'est l'enfant sélectionné, le désélectionner
          if (selectedChild.value && selectedChild.value.id === deletedChild.id) {
            selectedChild.value = null
          }
        }
      },
      `household_id=eq.${householdId}`
    )
  }

  function stopRealtimeSync() {
    console.log('[Children Store] Stopping realtime sync')
    realtimeSync.unsubscribeAll()
    currentHouseholdId = null
  }

  // Getters
  const allChildren = computed(() => children.value);
  const currentChild = computed(() => selectedChild.value);

  // Note: La synchronisation realtime pour children est gérée par AppContext Store
  // pour éviter les doublons de subscriptions. AppContext a les vraies données dans availableChildren.

  return {
    // State
    children,
    selectedChild,
    loading,
    error,

    // Actions
    loadChildren,
    getChildById,
    createChild,
    updateChild,
    deleteChild,
    selectChild,

    // Realtime sync
    startRealtimeSync,
    stopRealtimeSync,

    // Getters
    allChildren,
    currentChild
  };
});
